import { IBackgroundCheckRequestResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";
import { WSPageToolbarAction } from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";

import { openInNewTab } from "../../../../../../shared/utils/openInNewTab";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import {
  CellsConfig,
  ContextItem,
  RequirementTableCallback
} from "../../../../types";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";
import { ReportsInfoWidget } from "../../components/ReportsInfoWidget";
import { getBackgroundCheckPayerRequirementStatus } from "../helpers/getBackgroundCheckPayerRequirementStatus";
import { getBackgroundCheckProgressSteps } from "../helpers/getBackgroundCheckProgressSteps";
import { getBackgroundChecksRequirementName } from "../helpers/getBackgroundChecksRequirementName";

import { payeeBackgroundCheckRenderCardDescription } from "./payeeBackgroundCheckRenderCardDescription";

export const usePayeeBackgroundChecksConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  IBackgroundCheckRequestResponse | undefined
> => {
  const getManageReportAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    backgroundChecksRequest: IBackgroundCheckRequestResponse | undefined
  ) => {
    if (backgroundChecksRequest?.vendorMetadata.manageReportUrl) {
      return async () => {
        openInNewTab(backgroundChecksRequest.vendorMetadata.manageReportUrl!);
      };
    }
  };

  const getOpenReportAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    backgroundChecksRequest: IBackgroundCheckRequestResponse | undefined
  ) => {
    if (backgroundChecksRequest?.vendorMetadata.viewReportUrl) {
      return async () => {
        openInNewTab(backgroundChecksRequest.vendorMetadata.viewReportUrl!);
      };
    }
  };

  return {
    isActionRequired: requirement => {
      return false;
    },
    engagementsCell: getRequirementEngagementsTableCellProps,
    nameCell: (requirement, context, backgroundChecksRequest) => {
      return {
        text: getBackgroundChecksRequirementName(
          requirement,
          context,
          backgroundChecksRequest
        )
      };
    },
    requirementDetails: (requirement, context, backgroundChecksRequest) => {
      const manageReportAction = getManageReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      const openReportAction = getOpenReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      return {
        actions:
          manageReportAction || openReportAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Manage report",
                    kind: "Secondary",
                    onAsyncClick: manageReportAction,
                    visible: !!manageReportAction
                  },
                  {
                    label: "View report",
                    kind: "Secondary",
                    onAsyncClick: openReportAction,
                    visible: !!openReportAction
                  }
                ]
              }
            : undefined,
        children: payeeBackgroundCheckRenderCardDescription(
          requirement,
          backgroundChecksRequest
        ),
        data: [],
        header: {
          label: {
            text: getBackgroundChecksRequirementName(
              requirement,
              context,
              backgroundChecksRequest
            )
          }
        }
      };
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      backgroundChecksRequest
    ) => {
      return backgroundChecksRequest?.status
        ? getBackgroundCheckProgressSteps(backgroundChecksRequest.status)
        : undefined;
    },
    requirementDetailsWidgets: (
      requirement,
      context,
      backgroundChecksRequest
    ) => {
      if (backgroundChecksRequest?.backgroundCheckRequestSettingsId) {
        return (
          <ReportsInfoWidget
            backgroundCheckRequestSettingsId={
              backgroundChecksRequest.backgroundCheckRequestSettingsId
            }
          />
        );
      }

      return null;
    },
    requirementName: getBackgroundChecksRequirementName,
    requirementStatusAlert: (requirement, context, backgroundChecksRequest) => {
      return undefined;
    },
    statusCell: (requirement, context, backgroundChecksRequest) => {
      return getBackgroundCheckPayerRequirementStatus(
        requirement,
        context,
        backgroundChecksRequest
      );
    },
    toolbarActions: (requirement, context, backgroundChecksRequest) => {
      const actions: WSPageToolbarAction[] = [];

      const manageReportAction = getManageReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      const openReportAction = getOpenReportAction(
        requirement,
        context,
        backgroundChecksRequest
      );

      if (manageReportAction) {
        actions.push({
          buttonKind: "Secondary",
          icon: "open-tab",
          label: "Manage report",
          name: "Manage",
          onAsyncClick: manageReportAction
        });
      }

      if (openReportAction) {
        actions.push({
          buttonKind: "Secondary",
          icon: "open-tab",
          label: "View report",
          name: "View",
          onAsyncClick: openReportAction
        });
      }

      return actions;
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType
      })
  };
};
